import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore, formatNumber } from '../store/gameStore';

interface VideoRewardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  customTitle?: string;
  customDescription?: string;
  customRewardText?: string;
  rewardType?: 'gems' | 'multiplier';
}

// Hilfsfunktion für mm:ss Format
function formatSeconds(totalSeconds: number) {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

const VideoRewardModal: React.FC<VideoRewardModalProps> = ({ isOpen, onClose, onComplete, customTitle, customDescription, customRewardText, rewardType }) => {
  const { 
    videoRewards, 
    startVideoReward, 
    completeVideoReward, 
    canWatchVideo, 
    getVideoRewardTimeRemaining,
    getAccumulatedVideoTime,
    getMaxVideoTime
  } = useGameStore();
  
  const [videoProgress, setVideoProgress] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [showReward, setShowReward] = useState(false);

  // Reset states when modal opens
  useEffect(() => {
    if (isOpen) {
      setVideoProgress(0);
      setIsVideoPlaying(false);
      setShowReward(false);
    }
  }, [isOpen]);

  // Timer für Video-Fortschritt
  useEffect(() => {
    if (isVideoPlaying && videoProgress < 100) {
      const interval = setInterval(() => {
        setVideoProgress(prev => {
          if (prev >= 100) {
            setIsVideoPlaying(false);
            return 100;
          }
          return prev + 2; // 2% pro 100ms = 5 Sekunden für komplettes Video
        });
      }, 100);

      return () => clearInterval(interval);
    }
  }, [isVideoPlaying, videoProgress]);

  // Timer für Cooldown-Anzeige
  useEffect(() => {
    if (isOpen) {
      const interval = setInterval(() => {
        setTimeRemaining(getVideoRewardTimeRemaining());
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [isOpen, getVideoRewardTimeRemaining]);

  // Sekündlicher Timer für Live-Update der Videozeit
  const [timer, setTimer] = useState(0);
  useEffect(() => {
    if (isOpen) {
      const interval = setInterval(() => {
        setTimer(t => t + 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [isOpen]);

  // Zeit in Sekunden berechnen
  const accumulatedSeconds = Math.round(getAccumulatedVideoTime() * 60);
  const maxSeconds = getMaxVideoTime() * 60;
  const canWatch = canWatchVideo();

  // Für Edelstein-Reward: Nach Video kurz Reward anzeigen
  const handleComplete = () => {
    // Für Multiplikator-Videos: completeVideoReward aufrufen
    if (rewardType !== 'gems') {
      completeVideoReward();
    }
    
    setShowReward(true);
    setTimeout(() => {
      setShowReward(false);
      onComplete();
    }, 1200);
  };

  return (
    <AnimatePresence key="video-reward-modal">
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 modal-container"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-2 flex items-center justify-center gap-2">
                {customTitle ? customTitle : '📺 Video-Werbung'}
              </h2>
              <p className="text-gray-600 text-sm">
                {customDescription ? customDescription : 'Schaue ein Video für einen 2x Multiplikator!'}
              </p>
            </div>

            {/* Edelstein-Reward nach Video */}
            {showReward && rewardType === 'gems' && (
              <div className="flex flex-col items-center justify-center py-8">
                <div className="text-6xl mb-2 animate-bounce">💎</div>
                <div className="text-2xl font-bold text-purple-700 mb-2">
                  {customRewardText ? customRewardText : '+500 Edelsteine erhalten!'}
                </div>
              </div>
            )}

            {/* Aktueller Status */}
            {!showReward && (
              <>
                {/* Zeit- und Multiplikator-Anzeige nur für Multiplikator-Video */}
                {rewardType !== 'gems' && (
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4 mb-6">
                    <div className="text-center">
                      <div className="text-lg font-bold text-blue-600 mb-2">
                        Angesammelte Zeit: {formatSeconds(accumulatedSeconds)}/{formatSeconds(maxSeconds)}
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                        <div 
                          className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${(accumulatedSeconds / maxSeconds) * 100}%` }}
                        ></div>
                      </div>
                      <div className="text-sm text-gray-600">
                        {accumulatedSeconds > 0 ? '2x Multiplikator aktiv!' : 'Noch keine Zeit angesammelt'}
                      </div>
                    </div>
                  </div>
                )}

                {/* Video-Player Simulation */}
                {isVideoPlaying ? (
                  <div className="bg-gray-900 rounded-xl p-4 mb-6">
                    <div className="text-center text-white">
                      <div className="text-2xl mb-2">📺</div>
                      <div className="text-lg font-bold mb-2">Video läuft...</div>
                      <div className="w-full bg-gray-700 rounded-full h-3 mb-2">
                        <div 
                          className="bg-green-500 h-3 rounded-full transition-all duration-100"
                          style={{ width: `${videoProgress}%` }}
                        ></div>
                      </div>
                      <div className="text-sm text-gray-300">
                        {Math.round(videoProgress)}% abgeschlossen
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-100 rounded-xl p-4 mb-6">
                    <div className="text-center">
                      <div className="text-3xl mb-2">▶️</div>
                      <div className="text-lg font-bold text-gray-800 mb-2">
                        Video bereit
                      </div>
                      <div className="text-sm text-gray-600">
                        Klicke um das Video zu starten
                      </div>
                    </div>
                  </div>
                )}

                {/* Cooldown-Anzeige - nur für Multiplikator-Videos wenn Maximum erreicht */}
                {rewardType !== 'gems' && !canWatch && timeRemaining > 0 && accumulatedSeconds >= maxSeconds && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
                    <div className="text-center">
                      <div className="text-yellow-600 font-bold mb-1">
                        ⏰ Cooldown aktiv
                      </div>
                      <div className="text-sm text-yellow-700">
                        Nächstes Video in {timeRemaining} Minuten verfügbar
                      </div>
                    </div>
                  </div>
                )}

                {/* Maximum erreicht - nur für Multiplikator-Videos */}
                {rewardType !== 'gems' && accumulatedSeconds >= maxSeconds && (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
                    <div className="text-center">
                      <div className="text-green-600 font-bold mb-1">
                        🎉 Maximum erreicht!
                      </div>
                      <div className="text-sm text-green-700">
                        Du hast das Maximum von 2 Stunden angesammelt
                      </div>
                    </div>
                  </div>
                )}

                {/* Buttons */}
                <div className="flex gap-3">
                  {!isVideoPlaying && (rewardType === 'gems' ? true : canWatch && accumulatedSeconds < maxSeconds) ? (
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => {
                        // Für Edelstein-Videos: Direkt starten ohne startVideoReward()
                        if (rewardType === 'gems') {
                          setIsVideoPlaying(true);
                          setVideoProgress(0);
                        } else {
                          // Für Multiplikator-Videos: startVideoReward() verwenden
                          if (startVideoReward()) {
                            setIsVideoPlaying(true);
                            setVideoProgress(0);
                          }
                        }
                      }}
                      className="flex-1 bg-gradient-to-r from-blue-500 to-purple-500 text-white py-3 px-6 rounded-xl font-bold text-lg transition-all duration-300"
                    >
                      Video starten
                    </motion.button>
                  ) : isVideoPlaying ? (
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handleComplete}
                      disabled={videoProgress < 100}
                      className={`flex-1 py-3 px-6 rounded-xl font-bold text-lg transition-all duration-300 ${
                        videoProgress >= 100
                          ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      {videoProgress >= 100 ? (customRewardText ? customRewardText : 'Belohnung erhalten!') : 'Video läuft...'}
                    </motion.button>
                  ) : (
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={onClose}
                      className="flex-1 bg-gray-500 text-white py-3 px-6 rounded-xl font-bold text-lg transition-all duration-300"
                    >
                      Schließen
                    </motion.button>
                  )}
                </div>

                {/* Info */}
                {rewardType !== 'gems' && (
                  <div className="mt-4 text-xs text-gray-500 text-center">
                    <p>• Jedes Video gibt 10 Minuten 2x Multiplikator</p>
                    <p>• Maximum: 2 Stunden (120 Minuten)</p>
                    <p>• 5 Minuten Cooldown nur nach Erreichen des Maximums</p>
                  </div>
                )}
                {rewardType === 'gems' && (
                  <div className="mt-4 text-xs text-gray-500 text-center">
                    <p>• Schaue ein Video für 500 Edelsteine</p>
                    <p>• 5 Minuten Cooldown zwischen Videos</p>
                    <p>• Perfekt für Upgrades und Premium-Features</p>
                  </div>
                )}
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default VideoRewardModal; 